import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

/* ═══════════════════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════════════════ */
export interface ParsedAddress {
  address: string;
  city: string;
  province: string;
  postalCode: string;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    state?: string;
    postcode?: string;
    country?: string;
    county?: string;
    suburb?: string;
  };
}

interface Suggestion {
  id: string;
  mainText: string;
  secondaryText: string;
  parsed: ParsedAddress;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSelect: (parsed: ParsedAddress) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  sx?: object;
  error?: boolean;
}

/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const C = {
  navy:      "#0d1b2a",
  gold:      "#c9a96e",
  slateLt:   "#8a97a6",
  bsoft:     "#ede8e0",
  highlight: "#ffebd3",
  white:     "#ffffff",
  cream:     "#faf8f4",
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
   PROVINCE NAME → CODE MAP
   ═══════════════════════════════════════════════════════════════════════════ */
const PROVINCE_MAP: Record<string, string> = {
  "alberta": "AB",
  "british columbia": "BC",
  "manitoba": "MB",
  "new brunswick": "NB",
  "newfoundland and labrador": "NL",
  "nova scotia": "NS",
  "northwest territories": "NT",
  "nunavut": "NU",
  "ontario": "ON",
  "prince edward island": "PE",
  "quebec": "QC",
  "saskatchewan": "SK",
  "yukon": "YT",
};

function toProvinceCode(raw: string): string {
  if (!raw) return "";
  const lower = raw.trim().toLowerCase();
  // Already a 2-letter code
  if (lower.length === 2 && Object.values(PROVINCE_MAP).includes(raw.toUpperCase())) {
    return raw.toUpperCase();
  }
  return PROVINCE_MAP[lower] || raw;
}

/* ═══════════════════════════════════════════════════════════════════════════
   PARSE NOMINATIM RESULT → ParsedAddress
   ═══════════════════════════════════════════════════════════════════════════ */
function parseNominatim(result: NominatimResult): { suggestion: Suggestion } {
  const a = result.address;

  const houseNumber = a.house_number || "";
  const road = a.road || "";
  const address = `${houseNumber} ${road}`.trim();

  const city = a.city || a.town || a.village || a.municipality || "";
  const province = toProvinceCode(a.state || "");
  const postalCode = a.postcode || "";

  // Build display texts
  const mainText = address || result.display_name.split(",")[0] || "";
  const parts: string[] = [];
  if (city) parts.push(city);
  if (province) parts.push(province);
  if (postalCode) parts.push(postalCode);
  const secondaryText = parts.join(", ");

  return {
    suggestion: {
      id: String(result.place_id),
      mainText,
      secondaryText,
      parsed: { address, city, province, postalCode },
    },
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const AddressAutocomplete: React.FC<Props> = ({
  value,
  onChange,
  onSelect,
  label = "Address",
  placeholder = "Start typing an address...",
  size = "small",
  sx = {},
  error = false,
}) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  /* ── Fetch suggestions from Nominatim (300ms debounce) ── */
  const fetchSuggestions = useCallback((input: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (!input || input.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const params = new URLSearchParams({
          q: input,
          format: "json",
          addressdetails: "1",
          countrycodes: "ca",
          limit: "6",
        });

        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?${params}`,
          {
            signal: controller.signal,
            headers: { "Accept-Language": "en" },
          },
        );

        if (!res.ok) throw new Error("Nominatim request failed");

        const data: NominatimResult[] = await res.json();

        const mapped = data
          .map((r) => parseNominatim(r).suggestion)
          .filter((s) => s.mainText.length > 0);

        setSuggestions(mapped);
        setIsOpen(mapped.length > 0);
        setHighlightIdx(-1);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setSuggestions([]);
          setIsOpen(false);
        }
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }, []);

  /* ── Select a suggestion → fill form ── */
  const handleSelectSuggestion = useCallback(
    (suggestion: Suggestion) => {
      setIsOpen(false);
      setSuggestions([]);
      onChange(suggestion.parsed.address);
      onSelect(suggestion.parsed);
    },
    [onSelect, onChange],
  );

  /* ── Keyboard navigation ── */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === "Enter" && highlightIdx >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[highlightIdx]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  /* ── Input change ── */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    fetchSuggestions(val);
  };

  /* ═══════════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════════ */
  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box ref={anchorRef} sx={{ position: "relative" }}>
        <TextField
          label={label}
          placeholder={placeholder}
          fullWidth
          size={size}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
          error={error}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnOutlinedIcon sx={{ fontSize: 18, color: C.slateLt }} />
              </InputAdornment>
            ),
            endAdornment: isLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={16} thickness={4} sx={{ color: C.gold }} />
              </InputAdornment>
            ) : null,
          }}
          sx={sx}
        />

        {/* ── Suggestions dropdown ── */}
        <Popper
          open={isOpen && suggestions.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1500, width: anchorRef.current?.offsetWidth ?? "100%" }}
        >
          <Paper
            elevation={8}
            sx={{
              mt: 0.5,
              borderRadius: "12px",
              border: `1px solid ${C.bsoft}`,
              overflow: "hidden",
              maxHeight: 320,
              overflowY: "auto",
              boxShadow: "0 8px 32px rgba(13,27,42,0.14)",
            }}
          >
            {suggestions.map((s, idx) => (
              <Box
                key={s.id}
                onClick={() => handleSelectSuggestion(s)}
                onMouseEnter={() => setHighlightIdx(idx)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.2,
                  cursor: "pointer",
                  bgcolor: highlightIdx === idx ? C.highlight : C.white,
                  borderBottom: idx < suggestions.length - 1 ? `1px solid ${C.bsoft}` : "none",
                  transition: "background-color 0.1s",
                  "&:hover": { bgcolor: C.highlight },
                }}
              >
                <LocationOnOutlinedIcon
                  sx={{
                    fontSize: 20,
                    color: highlightIdx === idx ? C.gold : C.slateLt,
                    flexShrink: 0,
                    transition: "color 0.1s",
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13, fontWeight: 600, color: C.navy, lineHeight: 1.3,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    {s.mainText}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11, color: C.slateLt, lineHeight: 1.3,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    {s.secondaryText}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default AddressAutocomplete;
